import type { ShopPlanInfo } from "../../types/billing.types";

interface PlanStatusCardProps {
  plan: ShopPlanInfo | null;
  loading: boolean;
}

function formatResetDate(isoDate: string | null | undefined): string {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function PlanStatusCard({ plan, loading }: PlanStatusCardProps) {
  if (loading) {
    return <div className="glass-panel h-40 animate-pulse bg-white/60" />;
  }

  if (!plan) {
    return (
      <div className="glass-panel">
        <p className="text-sm text-slate-500">Unable to load plan information.</p>
      </div>
    );
  }

  const hasActiveSubscription =
    plan.subscription &&
    (plan.subscription.status === "active" || plan.subscription.status === "trialing");

  const usagePercent = plan.usage.max_assets_per_month > 0
    ? Math.min(100, Math.round((plan.usage.assets_generated_this_month / plan.usage.max_assets_per_month) * 100))
    : 0;

  const planCode = plan.plan?.code;
  const showPremiumRow =
    hasActiveSubscription &&
    planCode !== "scale" &&
    planCode !== "admin" &&
    plan.usage.premium_generations_included > 0;

  const premiumPercent = plan.usage.premium_generations_included > 0
    ? Math.min(100, Math.round((plan.usage.premium_generations_used / plan.usage.premium_generations_included) * 100))
    : 0;

  const resetDate = formatResetDate(plan.usage.billing_period_reset_at);

  return (
    <div className="glass-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-slate-400">Current plan</p>
          <h2 className="mt-2 text-2xl font-semibold text-slate-900">
            {hasActiveSubscription && plan.plan ? plan.plan.name : "No active plan"}
          </h2>
          {hasActiveSubscription && plan.plan && (
            <p className="text-sm text-slate-500">
              ${plan.plan.monthly_price.toFixed(2)}/month
            </p>
          )}
        </div>
        <span
          className={
            hasActiveSubscription
              ? "rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600"
              : "rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-600"
          }
        >
          {hasActiveSubscription ? "Active" : "Trial / paused"}
        </span>
      </div>

      {hasActiveSubscription && plan.plan ? (
        <div className="mt-6 space-y-4">
          {/* AR products usage */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>AR products used this month</span>
              <span className="font-semibold text-slate-700">
                {plan.usage.assets_generated_this_month} / {plan.usage.max_assets_per_month}
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 transition-all"
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          </div>

          {/* Premium generations usage (Growth only) */}
          {showPremiumRow && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Premium Generations used this month</span>
                <span className="font-semibold text-slate-700">
                  {plan.usage.premium_generations_used} / {plan.usage.premium_generations_included}
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all"
                  style={{ width: `${premiumPercent}%` }}
                />
              </div>
            </div>
          )}

          {/* Next reset */}
          {resetDate && (
            <p className="text-xs text-slate-400">
              Next reset: {resetDate}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-4 text-sm text-slate-500">
          Subscribe to unlock AR generation and keep this interface glowing.
        </p>
      )}
    </div>
  );
}
